import React from "react"
import { TruckIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Img from "./Img"
import ProductRating from "./ProductRating"
import FavoriteButton from "./FavoriteButton"
import { ProductPrice, authorNames } from "./ProductBox"
import useT from "../hooks/useT"
import { analyzeRatings } from "../utils/ratings"
import { buildContentUrl } from "../utils/contentRoutes"
import { useProfileName } from "../hooks/useCatalogData"
import { isPhysicalProduct, isOutOfStock, isSoldInCoins } from "../utils/productUtils"
import { usePlacement } from "../store/placements"
import { listOf, trackListImpression, trackSelectItem } from "../tracking"
import { addToCart, useCart, cartQuantityOf } from "../store/cart"
import { needsSelection } from "aldoo-ra/Store/extensions"
import { navigate } from "vike/client/router"

// List-view row for the category browser
export default function ProductRow({ product, placement: ownPlacement }) {
  const placement = usePlacement(ownPlacement)
  // a row of a list on screen, and opened from it (GA4 view_item_list / select_item)
  const list = listOf(placement)
  React.useEffect(() => {
    if (product?.pid && list) trackListImpression(product, list)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.pid, list?.id])
  const t = useT()
  const cart = useCart()
  const typeName = useProfileName(product.profile)
  const { avgRating, numRatings } = analyzeRatings(product.ratings)
  const url = buildContentUrl(product)
  const authors = authorNames(product)
  const outOfStock = isOutOfStock(product, cartQuantityOf(cart, product.pid))
  const canAddToCart =
    !product.free_access && (product.real_money_cost || product.real_price) && !isSoldInCoins(product) && !product.is_bought

  const handleAddToCart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // a product the buyer has to choose for is chosen on its page
    if (needsSelection(product)) return navigate(url)
    // the cart says "Added to cart" itself (store/cart.js)
    addToCart(product, 1, {}, { placement })
  }

  return (
    <Button variant="none"
      href={url}
      onClick={() => list && trackSelectItem(product, list)}
      // one product to a line: on a phone the picture and the text, with the
      // price and Buy under them; on desktop the three columns side by side
      className="relative flex flex-row w-full md:min-h-[200px] bg-paper rounded-lg p-4 lg:p-6 border border-bordered transition-all hover:shadow-md"
    >
      {/* the same 180:222 box as the cards, the picture whole in it */}
      <div className="relative w-[110px] md:w-[180px] aspect-[180/222] shrink-0 mr-3 md:mr-4 flex items-start overflow-hidden bg-paper">
        <Img src={product.original || product.thumb || product.square} alt={product.name} width={180} height={222} fit="contain" className="w-full h-auto" />
        {product.free_shipping && isPhysicalProduct(product) && (
          <span className="absolute top-1 left-1 bg-success text-white rounded px-1 py-0.5">
            <TruckIcon className="w-3 h-3" />
          </span>
        )}
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 flex flex-col gap-1 min-w-0">
          <Typography 
            component="h4"
            variant="h5"
            className="w-full"
          >
            {product.name}
          </Typography>

          {/* the author right under the title, as on the cards */}
          {authors && <Typography variant="txt">
                        {authors}
                      </Typography>
          }
          {numRatings > 0 && <ProductRating 
                              avgRating={avgRating} 
                              numRatings={numRatings} 
                            />
          }
          {typeName && <Typography variant="txt2_dim">{typeName}</Typography>}
        </div>

        <div className="flex flex-row md:flex-col items-end md:items-end justify-between md:justify-end md:min-w-[150px] md:flex-1 gap-3 mt-auto md:mt-0 pt-2 md:pt-0">
          <ProductPrice product={product} align="end" size="medium" />
          <div className="flex items-center gap-2">
            {/* the account's Favorites, in a box of Buy's height */}
            <FavoriteButton
              product={product}
              fillOnHover
              className="w-[34px] h-[34px] md:w-10 md:h-10 rounded bg-theme_black text-white hover:text-white transition-colors"
            />
            {canAddToCart && !outOfStock && (
              <Button variant="primary_dark_s" className="md:hidden" onClick={handleAddToCart}>
                {t`Buy`}
              </Button>
            )}
            {canAddToCart && !outOfStock && (
              <Button variant="primary_dark" className="hidden md:inline-flex" onClick={handleAddToCart}>
                {t`Buy`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Button>
  )
}
